/**
 * Custom ring buffer-based analyzer for time-domain audio visualization
 * Replaces Web Audio's AnalyserNode for waveform/lissajous displays
 */

import { RING_BUFFER_SIZE } from './bytecode.ts'

export class RingAnalyser {
  private leftBuffer: Float32Array
  private rightBuffer: Float32Array
  private writePtr: Int32Array
  private bufferSize: number
  private readPtr = 0
  private sampleRate: number
  private safetyLag = 2048 // Stay this many samples behind write pointer
  private initialized = false

  constructor(
    ringBufferLeft: SharedArrayBuffer,
    ringBufferRight: SharedArrayBuffer,
    ringBufferControl: SharedArrayBuffer,
    sampleRate = 48000,
  ) {
    this.leftBuffer = new Float32Array(ringBufferLeft)
    this.rightBuffer = new Float32Array(ringBufferRight)
    this.writePtr = new Int32Array(ringBufferControl)
    this.bufferSize = RING_BUFFER_SIZE
    this.sampleRate = sampleRate
  }

  /**
   * Initialize read pointer on first read
   */
  private initReadPointer(): void {
    if (this.initialized) return

    const currentWritePtr = Atomics.load(this.writePtr, 0)
    this.readPtr = (currentWritePtr - this.safetyLag + this.bufferSize) % this.bufferSize
    this.initialized = true
  }

  /**
   * Get number of samples available to read since last read
   * This tells us how many samples have been written between frames
   */
  getAvailableSamples(): number {
    this.initReadPointer()

    const currentWritePtr = Atomics.load(this.writePtr, 0)
    const distance = (currentWritePtr - this.readPtr + this.bufferSize) % this.bufferSize

    // Keep safety lag - don't read all the way to write pointer
    const availableWithSafety = Math.max(0, distance - this.safetyLag)

    // Cap at reasonable amount to avoid overruns
    return Math.min(availableWithSafety, this.bufferSize / 2)
  }

  /**
   * Advance read pointer by samples read
   */
  private advanceReadPointer(samplesRead: number): void {
    this.readPtr = (this.readPtr + samplesRead) % this.bufferSize
  }

  /**
   * Get time domain data for both channels (stereo)
   * Returns the most recent N samples with smooth, constant-rate reading
   */
  getFloatTimeDomainData(destArray: Float32Array): void {
    this.initReadPointer()
    const samplesNeeded = destArray.length

    // Read forward from read pointer, wrapping around
    for (let i = 0; i < samplesNeeded; i++) {
      const readIndex = (this.readPtr + i) % this.bufferSize
      // Average left and right channels for mono output
      destArray[i] = (this.leftBuffer[readIndex] + this.rightBuffer[readIndex]) * 0.5
    }

    this.advanceReadPointer(samplesNeeded)
  }

  /**
   * Get time domain data for a specific channel
   */
  getFloatTimeDomainDataChannel(destArray: Float32Array, channel: 'left' | 'right'): void {
    this.initReadPointer()
    const samplesNeeded = destArray.length
    const sourceBuffer = channel === 'left' ? this.leftBuffer : this.rightBuffer

    for (let i = 0; i < samplesNeeded; i++) {
      const readIndex = (this.readPtr + i) % this.bufferSize
      destArray[i] = sourceBuffer[readIndex]
    }

    this.advanceReadPointer(samplesNeeded)
  }

  /**
   * Get time domain data in byte format (0-255) for Web Audio compatibility
   */
  getByteTimeDomainData(destArray: Uint8Array): void {
    this.initReadPointer()
    const samplesNeeded = destArray.length

    for (let i = 0; i < samplesNeeded; i++) {
      const readIndex = (this.readPtr + i) % this.bufferSize
      // Average left and right channels, then convert to 0-255 range
      const sample = (this.leftBuffer[readIndex] + this.rightBuffer[readIndex]) * 0.5
      destArray[i] = Math.floor((sample + 1) * 127.5)
    }

    this.advanceReadPointer(samplesNeeded)
  }

  /**
   * Get time domain data in byte format for a specific channel
   */
  getByteTimeDomainDataChannel(destArray: Uint8Array, channel: 'left' | 'right'): void {
    this.initReadPointer()
    const samplesNeeded = destArray.length
    const sourceBuffer = channel === 'left' ? this.leftBuffer : this.rightBuffer

    for (let i = 0; i < samplesNeeded; i++) {
      const readIndex = (this.readPtr + i) % this.bufferSize
      const sample = sourceBuffer[readIndex]
      destArray[i] = Math.floor((sample + 1) * 127.5)
    }

    this.advanceReadPointer(samplesNeeded)
  }

  /**
   * Get stereo time domain data (separate left/right arrays)
   */
  getStereoTimeDomainData(leftDest: Float32Array, rightDest: Float32Array): void {
    this.initReadPointer()
    const samplesNeeded = Math.min(leftDest.length, rightDest.length)

    for (let i = 0; i < samplesNeeded; i++) {
      const readIndex = (this.readPtr + i) % this.bufferSize
      leftDest[i] = this.leftBuffer[readIndex]
      rightDest[i] = this.rightBuffer[readIndex]
    }

    this.advanceReadPointer(samplesNeeded)
  }

  /**
   * Get stereo time domain data in byte format (0-255)
   */
  getStereoByteTimeDomainData(leftDest: Uint8Array, rightDest: Uint8Array): void {
    this.initReadPointer()
    const samplesNeeded = Math.min(leftDest.length, rightDest.length)

    for (let i = 0; i < samplesNeeded; i++) {
      const readIndex = (this.readPtr + i) % this.bufferSize
      leftDest[i] = Math.floor((this.leftBuffer[readIndex] + 1) * 127.5)
      rightDest[i] = Math.floor((this.rightBuffer[readIndex] + 1) * 127.5)
    }

    this.advanceReadPointer(samplesNeeded)
  }

  /**
   * Get all available samples since last read (for scrolling visualizers)
   * Returns actual number of samples read
   */
  getAllAvailableSamples(destArray: Float32Array): number {
    const available = this.getAvailableSamples()
    const toRead = Math.min(available, destArray.length)

    if (toRead === 0) return 0

    for (let i = 0; i < toRead; i++) {
      const readIndex = (this.readPtr + i) % this.bufferSize
      // Average left and right channels for mono output
      destArray[i] = (this.leftBuffer[readIndex] + this.rightBuffer[readIndex]) * 0.5
    }

    this.advanceReadPointer(toRead)
    return toRead
  }

  /**
   * Peek at the most recent N samples without advancing read pointer
   * Useful for waveform/lissajous displays that show a window
   */
  peekRecentSamples(destArray: Float32Array): void {
    const currentWritePtr = Atomics.load(this.writePtr, 0)
    const samplesNeeded = destArray.length

    // Read backwards from write pointer (with safety lag)
    const startPtr = (currentWritePtr - this.safetyLag - samplesNeeded + this.bufferSize) % this.bufferSize

    for (let i = 0; i < samplesNeeded; i++) {
      const readIndex = (startPtr + i) % this.bufferSize
      destArray[i] = (this.leftBuffer[readIndex] + this.rightBuffer[readIndex]) * 0.5
    }
  }

  /**
   * Peek at stereo samples without advancing read pointer
   */
  peekRecentStereoSamples(leftDest: Float32Array, rightDest: Float32Array): void {
    const currentWritePtr = Atomics.load(this.writePtr, 0)
    const samplesNeeded = Math.min(leftDest.length, rightDest.length)

    // Read backwards from write pointer (with safety lag)
    const startPtr = (currentWritePtr - this.safetyLag - samplesNeeded + this.bufferSize) % this.bufferSize

    for (let i = 0; i < samplesNeeded; i++) {
      const readIndex = (startPtr + i) % this.bufferSize
      leftDest[i] = this.leftBuffer[readIndex]
      rightDest[i] = this.rightBuffer[readIndex]
    }
  }

  /**
   * Peek at stereo samples in byte format without advancing read pointer
   */
  peekRecentStereoByteData(leftDest: Uint8Array, rightDest: Uint8Array): void {
    const currentWritePtr = Atomics.load(this.writePtr, 0)
    const samplesNeeded = Math.min(leftDest.length, rightDest.length)

    // Read backwards from write pointer (with safety lag)
    const startPtr = (currentWritePtr - this.safetyLag - samplesNeeded + this.bufferSize) % this.bufferSize

    for (let i = 0; i < samplesNeeded; i++) {
      const readIndex = (startPtr + i) % this.bufferSize
      leftDest[i] = Math.floor((this.leftBuffer[readIndex] + 1) * 127.5)
      rightDest[i] = Math.floor((this.rightBuffer[readIndex] + 1) * 127.5)
    }
  }

  /**
   * Reset read pointer to resync with write pointer
   * Call this when playback starts or after discontinuities
   */
  reset(): void {
    this.initialized = false
    this.readPtr = 0
  }
}

